import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Import from your new API structure
import { userAPI } from "../../helpers/api/users";

// Fetch single user
export const getUser = createAsyncThunk(
  "users/getUser", 
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userAPI.getById(userId);
      return response;
    } catch (error) {
      toast.error("Failed to fetch user details", { autoClose: 3000 });
      return rejectWithValue(error.message || "Failed to fetch user");
    }
  }
);

// Fetch all users
export const getUsers = createAsyncThunk(
  "users/getUsers", 
  async (_, { rejectWithValue }) => {
    try {
      const response = await userAPI.getAll();
      return response;
    } catch (error) {
      toast.error("Failed to fetch users", { autoClose: 3000 });
      return rejectWithValue(error.message || "Failed to fetch users");
    }
  }
);

// Add new user
export const addNewUser = createAsyncThunk(
  "users/addNewUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await userAPI.create(userData);
      toast.success("User Added Successfully", { autoClose: 3000 });
      return response;
    } catch (error) {
      toast.error("User Addition Failed", { autoClose: 3000 });
      return rejectWithValue(error.message || "Failed to add user");
    }
  }
);

// Update user
export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ id, ...userData }, { rejectWithValue }) => {
    try {
      const response = await userAPI.update(id, userData);
      toast.success("User Updated Successfully", { autoClose: 3000 });
      return response;
    } catch (error) {
      toast.error("User Update Failed", { autoClose: 3000 });
      return rejectWithValue(error.message || "Failed to update user");
    }
  }
);

// Delete user
export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (userId, { rejectWithValue }) => {
    try {
      await userAPI.delete(userId);
      toast.success("User Deleted Successfully", { autoClose: 3000 });
      return userId; // Return the ID for reducer to remove from state
    } catch (error) {
      toast.error("User Deletion Failed", { autoClose: 3000 });
      return rejectWithValue(error.message || "Failed to delete user");
    }
  }
);

// Change password (current user)
export const changePassword = createAsyncThunk(
  "users/changePassword",
  async ({ old_password, new_password1, new_password2 }, { rejectWithValue }) => {
    try {
      // Client-side validation
      if (new_password1 !== new_password2) {
        toast.error("Passwords do not match", { autoClose: 3000 });
        return rejectWithValue("Passwords do not match");
      }

      const response = await userAPI.changePassword({ 
        old_password, 
        new_password1, 
        new_password2 
      });
      
      toast.success("Password Changed Successfully", { autoClose: 3000 });
      return response;
    } catch (error) {
      toast.error("Password Change Failed", { autoClose: 3000 });
      return rejectWithValue(error.message || "Failed to change password");
    }
  }
);

// Change user password (admin action)
export const changeUserPassword = createAsyncThunk(
  "users/changeUserPassword",
  async ({ user_id, password, password_verify }, { rejectWithValue }) => {
    try {
      const response = await userAPI.changePassword(user_id, { 
        password, 
        password_verify 
      });
      
      toast.success("Password Changed Successfully", { autoClose: 3000 });
      return response;
    } catch (error) {
      toast.error("Password Change Failed", { autoClose: 3000 });
      return rejectWithValue(error.message || "Failed to change user password");
    }
  }
);

// Get user profile
export const getUserProfile = createAsyncThunk(
  "users/getUserProfile", 
  async (_, { rejectWithValue }) => {
    try {
      const response = await userAPI.getProfile();
      return response;
    } catch (error) {
      toast.error("Failed to fetch user profile", { autoClose: 3000 });
      return rejectWithValue(error.message || "Failed to fetch profile");
    }
  }
);

// Update user profile
export const updateUserProfile = createAsyncThunk(
  "users/updateUserProfile",
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await userAPI.updateProfile(profileData);
      toast.success("Profile Updated Successfully", { autoClose: 3000 });
      return response;
    } catch (error) {
      toast.error("Profile Update Failed", { autoClose: 3000 });
      return rejectWithValue(error.message || "Failed to update profile");
    }
  }
);

// Update preferred currency only (lightweight — no success toast to avoid noise)
export const updatePreferredCurrency = createAsyncThunk(
  "users/updatePreferredCurrency",
  async (currency, { rejectWithValue }) => {
    try {
      const response = await userAPI.updateProfile({ preferred_currency: currency });
      return response.preferred_currency;
    } catch (error) {
      toast.error("Failed to save currency preference", { autoClose: 3000 });
      return rejectWithValue(error.message);
    }
  }
);